import { BotId } from '@feedyou-ai/feedbot-schema'
import { GeneralErrors, isFeedieEmail } from '@feedyou/utils'
import { environmentGroupForBotSelector } from '@features/serverBots/selectors/environmentGroupForBotSelector'

import { FeedbotThunk } from '../../../../../store/types'
import { confirmDeletion, confirmDeletionDespiteNonNativeHostingId } from './confirmDeletion'
import { userInfoSelector } from '../../../../../store/user'
import { doesBotHaveNativeApiUrl } from '../utils'
import { callAsyncWithRetry, FAILED } from '../../../../../lib/async/callAsyncWithRetry'
import { getFullBotMetadataForBot } from '../../getterThunks/getFullBotMetadataForBot'
import { endpointHelpers } from '../../../endpointHelpers'
import { botMetadataSelector } from '../../../selectors/botMetadataSelector'
import { botMetadataForEnvironmentSelector } from '../../../selectors/botMetadataForEnvironmentSelector'
import {
  alertHavingUnsavedChanges,
  alertHavingUnsavedChangesForTestEnvironment
} from '../../../utils/bootbox'

export const initiateBotDelete =
  (botId: BotId): FeedbotThunk<Promise<'success' | 'failure' | 'abort'>> =>
  async (dispatch, getState) => {
    const email = userInfoSelector(getState()).email
    const metadata = await dispatch(getFullBotMetadataForBot(botId))
    const hasDev = await dispatch(hasDevEnv(botId))

    const { testName } = environmentGroupForBotSelector(botId)(getState())

    const { confirmed, immediate } = await confirmDeletion(
      botId,
      isFeedieEmail(email),
      hasDev,
      testName
    )

    if (!confirmed) {
      return 'abort'
    }

    if (!doesBotHaveNativeApiUrl(botId, metadata?.apiUrl)) {
      const isConfirmed = await confirmDeletionDespiteNonNativeHostingId(botId, metadata?.apiUrl!)
      if (!isConfirmed) {
        return 'abort'
      }
    }

    const result = await callAsyncWithRetry({
      actionDescription: 'delete',
      asyncFunction: () =>
        dispatch(endpointHelpers.deleteBot.initiate({ botId, force: immediate }))
          .unwrap()
          .catch(err => {
            if (err.data === GeneralErrors.UnsavedBotChanges) {
              alertHavingUnsavedChanges()
              return 'success'
            }
            if (err.data === GeneralErrors.UnsavedBotTestEnvironmentChanges) {
              alertHavingUnsavedChangesForTestEnvironment(testName)
              return 'success'
            }
            return 'failure'
          })
    })

    return result === FAILED ? 'failure' : 'success'
  }

const hasDevEnv =
  (id: BotId): FeedbotThunk<Promise<boolean>> =>
  async (dispatch, getState) => {
    const botMetadata = botMetadataSelector(id)(getState())

    return (
      !!botMetadata &&
      botMetadata.environmentType === 'prod' &&
      !!botMetadataForEnvironmentSelector(botMetadata.environmentGroupId, 'test')(getState())
    )
  }
